import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import {
  fetchCustomerSelectOptions,
  mergeCustomerSelectOption,
} from '#/api/crmMgmt/customer/fetchSelectOptions';
import { customerOrderAttachmentsApi } from '#/api/crmMgmt/customerOrder/attachments';
import { createCustomerOrder } from '#/api/crmMgmt/customerOrder/create';
import { fetchCustomerOrderDetail } from '#/api/crmMgmt/customerOrder/fetchDetail';
import { fetchCurrencySelectOptions } from '#/api/system/currency';
import { updateCustomerOrder } from '#/api/crmMgmt/customerOrder/update';
import {
  fetchProductSelectOptions,
  mergeProductSelectOption,
} from '#/api/crmMgmt/product/fetchSelectOptions';
import { ATTACHMENT_POLICY_SPREADSHEET } from '#/api/platform/attachments';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import {
  DocumentAttachmentsCard,
  uploadPendingAttachmentsAfterCreate,
} from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import {
  ensureSelectOption,
  mapCustomerAddressSelectOptions,
  mapCustomerContactSelectOptions,
  pickDefaultCustomerAddressId,
  pickDefaultCustomerContactId,
} from '#/modules/crmMgmt/customer/helper/customerAddress';
import CustomerOrderLinesEditor from '#/modules/crmMgmt/customerOrder/components/CustomerOrderLinesEditor';
import CustomerOrderStatusBadge from '#/modules/crmMgmt/customerOrder/components/CustomerOrderStatusBadge';
import {
  getCustomerOrderApiErrorMessage,
  isCustomerOrderVersionConflict,
} from '#/modules/crmMgmt/customerOrder/helper/apiError';
import { canEditCustomerOrderDraft } from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';
import {
  buildCustomerOrderWritePayload,
  mapCustomerOrderDetailToFormValues,
} from '#/modules/crmMgmt/customerOrder/helper/formSerialize';
import {
  CUSTOMER_ORDER_FIELD_LABELS,
  CUSTOMER_ORDER_ROUNDING_MODE_OPTIONS,
  CUSTOMER_ORDER_TAX_TYPE_OPTIONS,
  CUSTOMER_ORDER_TAX_TYPE_TIP,
  CUSTOMER_ORDER_ZERO_TAX_RATE_TYPES,
  customerOrderFormSchema,
  resolveCustomerOrderFieldLabel,
} from '#/modules/crmMgmt/customerOrder/schemas/customerOrderSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import { applyDocumentCurrencyFieldError } from '#/utils/documentCurrencyError';
import {
  customerDetailQueryKey,
  customerOrderDetailQueryKey,
  customerOrderListQueryKeyPrefix,
  lookupQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const DEFAULT_VALUES = {
  customerOrderDate: '',
  customerId: '',
  billingAddressId: '',
  shippingAddressId: '',
  shippingContactId: '',
  currencyId: '',
  taxType: 'TAXABLE',
  taxRate: 0.05,
  roundingMode: '',
  issueReason: '',
  remark: '',
  lines: [],
  manualLines: [],
};

/** 幣別選單至少 12rem，`{code} - {name}` 不被截斷 */
const CURRENCY_SELECT_STYLES = {
  menu: (base) => ({ ...base, minWidth: '12rem' }),
};

const CreateEditCustomerOrderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const updatedAtRef = useRef(null);
  const currencyIdRef = useRef('');
  const customerIdRef = useRef('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: customerOrderDetailQueryKey(id),
    queryFn: () => fetchCustomerOrderDetail(id),
    enabled: Boolean(id),
  });

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const currencyOptionsQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
  });

  async function createCustomerOrderWithAttachments(payload) {
    const result = await createCustomerOrder(payload);
    const createdId = result?.data?.id;
    if (createdId) {
      await uploadPendingAttachmentsAfterCreate(
        customerOrderAttachmentsApi,
        String(createdId),
        pendingFiles,
      );
    }
    return result;
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createCustomerOrderWithAttachments,
    updateFn: updateCustomerOrder,
    buildPayload: (values) => buildCustomerOrderWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
      isEdit: Boolean(id),
      originalCurrencyId: currencyIdRef.current,
    }),
    listQueryKeyPrefix: customerOrderListQueryKeyPrefix,
    detailQueryKey: customerOrderDetailQueryKey,
    getDetailPath: ROUTES.customerOrderDetail,
    afterUpdateNavigateTo: id ? ROUTES.customerOrderDetail(id) : null,
    getErrorMessage: (error) => getCustomerOrderApiErrorMessage(error, '儲存失敗'),
    onError: (error) => applyDocumentCurrencyFieldError(error, setError),
    isVersionConflict: isCustomerOrderVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(customerOrderFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const detail = detailQuery.data?.data;
  const customerId = useWatch({ control, name: 'customerId', defaultValue: '' });
  const billingAddressId = useWatch({
    control,
    name: 'billingAddressId',
    defaultValue: '',
  });
  const shippingAddressId = useWatch({
    control,
    name: 'shippingAddressId',
    defaultValue: '',
  });
  const shippingContactId = useWatch({
    control,
    name: 'shippingContactId',
    defaultValue: '',
  });

  const customerDetailQuery = useQuery({
    queryKey: customerDetailQueryKey(customerId),
    queryFn: () => fetchCustomerDetail(customerId),
    enabled: Boolean(customerId),
  });

  const productOptionsQuery = useQuery({
    queryKey: lookupQueryKey('productSelect', { customerId: customerId || null }),
    queryFn: () => fetchProductSelectOptions({ customerId }),
    enabled: Boolean(customerId),
  });

  useEffect(() => {
    if (!isEdit || !detail) {
      return;
    }

    if (!canEditCustomerOrderDraft(detail.status)) {
      toast.warning('僅草稿可編輯，已返回詳情');
      navigate(ROUTES.customerOrderDetail(id), { replace: true });
    }
  }, [isEdit, detail, id, navigate]);

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.record?.updatedAt ?? detail.updatedAt ?? null;
    currencyIdRef.current = detail.currencyId ?? '';
    const formValues = mapCustomerOrderDetailToFormValues(detail);
    customerIdRef.current = formValues.customerId ?? '';
    resetValidationSummary();
    reset(formValues);
  }, [detail, reset, resetValidationSummary]);

  const customerAddresses = useMemo(
    () => customerDetailQuery.data?.data?.addresses ?? [],
    [customerDetailQuery.data],
  );

  const customerContacts = useMemo(
    () => customerDetailQuery.data?.data?.contacts ?? [],
    [customerDetailQuery.data],
  );

  const customerOptions = useMemo(() => {
    const options = mergeCustomerSelectOption(
      customerOptionsQuery.data ?? [],
      detail?.customer,
    );
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerOptionsQuery.data, detail?.customer]);

  const productOptions = useMemo(() => {
    let options = productOptionsQuery.data ?? [];
    (detail?.lines ?? []).forEach((line) => {
      options = mergeProductSelectOption(options, line.product);
    });
    return options;
  }, [productOptionsQuery.data, detail?.lines]);

  const billingAddressOptions = useMemo(() => {
    const options = mapCustomerAddressSelectOptions(customerAddresses, 'BILLING');
    return ensureSelectOption(options, billingAddressId);
  }, [customerAddresses, billingAddressId]);

  const shippingAddressOptions = useMemo(() => {
    const options = mapCustomerAddressSelectOptions(customerAddresses, 'SHIPPING');
    return ensureSelectOption(options, shippingAddressId);
  }, [customerAddresses, shippingAddressId]);

  const shippingContactOptions = useMemo(() => {
    const options = mapCustomerContactSelectOptions(customerContacts);
    const withBlank = [
      { value: '', label: '（未指定／系統預設第一筆）' },
      ...options,
    ];
    const snapshotContact = detail?.customerSnapshot?.shippingContact;
    let snapshotLabel;
    if (snapshotContact) {
      snapshotLabel = [
        snapshotContact.name,
        snapshotContact.department,
        snapshotContact.title,
      ].filter(Boolean).join(' · ') || snapshotContact.id;
    }
    return ensureSelectOption(withBlank, shippingContactId, snapshotLabel);
  }, [
    customerContacts,
    shippingContactId,
    detail?.customerSnapshot?.shippingContact,
  ]);

  useEffect(() => {
    if (!customerId || customerDetailQuery.isLoading || customerDetailQuery.isError) {
      return;
    }

    const addresses = customerDetailQuery.data?.data?.addresses ?? [];
    const contacts = customerDetailQuery.data?.data?.contacts ?? [];
    const currentBilling = getValues('billingAddressId');
    const currentShipping = getValues('shippingAddressId');
    const currentContact = getValues('shippingContactId');
    const billingIds = new Set(
      mapCustomerAddressSelectOptions(addresses, 'BILLING').map((option) => option.value),
    );
    const shippingIds = new Set(
      mapCustomerAddressSelectOptions(addresses, 'SHIPPING').map((option) => option.value),
    );
    const contactIds = new Set(
      mapCustomerContactSelectOptions(contacts).map((option) => option.value),
    );

    if (!currentBilling || !billingIds.has(currentBilling)) {
      setValue(
        'billingAddressId',
        pickDefaultCustomerAddressId(addresses, 'BILLING'),
        { shouldDirty: Boolean(currentBilling), shouldValidate: true },
      );
    }

    if (!currentShipping || !shippingIds.has(currentShipping)) {
      setValue(
        'shippingAddressId',
        pickDefaultCustomerAddressId(addresses, 'SHIPPING'),
        { shouldDirty: Boolean(currentShipping), shouldValidate: true },
      );
    }

    if (currentContact && !contactIds.has(currentContact)) {
      setValue(
        'shippingContactId',
        pickDefaultCustomerContactId(contacts),
        { shouldDirty: true, shouldValidate: true },
      );
    } else if (!currentContact && contacts.length > 0 && !isEdit) {
      setValue(
        'shippingContactId',
        pickDefaultCustomerContactId(contacts),
        { shouldDirty: false, shouldValidate: true },
      );
    }
  }, [
    customerId,
    customerDetailQuery.data,
    customerDetailQuery.isLoading,
    customerDetailQuery.isError,
    getValues,
    setValue,
    isEdit,
  ]);

  function handleCustomerChange(nextId) {
    const next = nextId ?? '';
    const prev = customerIdRef.current ?? '';
    if (next === prev) {
      return;
    }

    customerIdRef.current = next;
    setValue('billingAddressId', '', { shouldDirty: true, shouldValidate: true });
    setValue('shippingAddressId', '', { shouldDirty: true, shouldValidate: true });
    setValue('shippingContactId', '', { shouldDirty: true, shouldValidate: true });
    const lines = getValues('lines') ?? [];
    lines.forEach((_, index) => {
      setValue(`lines.${index}.productId`, '', { shouldDirty: true, shouldValidate: true });
      setValue(`lines.${index}.salesUnitName`, '', { shouldDirty: true });
    });
  }

  function handleTaxTypeChange(nextType) {
    if (CUSTOMER_ORDER_ZERO_TAX_RATE_TYPES.has(nextType)) {
      setValue('taxRate', 0, { shouldDirty: true, shouldValidate: true });
      return;
    }
    if (nextType === 'TAX_INCLUDED') {
      const currentRate = Number(getValues('taxRate'));
      if (!Number.isFinite(currentRate) || currentRate <= 0) {
        setValue('taxRate', 0.05, { shouldDirty: true, shouldValidate: true });
      }
    }
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯客戶訂單' : '新增客戶訂單';
  const headerStatus = isEdit ? (detail?.status || 'DRAFT') : 'DRAFT';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditCustomerOrderDraft(detail.status);
  const addressSelectDisabled = !customerId || customerDetailQuery.isLoading;
  const isProductSelectDisabled = !customerId || productOptionsQuery.isLoading;
  const productNoOptionsMessage = customerId ? '此客戶無啟用產品' : '請先選擇客戶';

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <div className="flex items-center gap-2">
              <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
                <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
                <CustomerOrderStatusBadge status={headerStatus} size="sm" />
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.customerOrder}>返回列表</Link>
              </Button>
            </div>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getCustomerOrderApiErrorMessage(detailQuery.error, '找不到客戶訂單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <CustomerOrderStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.customerOrderDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          ) : null}

          {!isPageLoading && !isDetailNotFound && !isReadOnlyEdit ? (
            <div className="space-y-4">
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={CUSTOMER_ORDER_FIELD_LABELS}
                fieldLabelResolver={resolveCustomerOrderFieldLabel}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500 pb-0!">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">單據資訊</h2>
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingDateInput
                        id="customerOrderDate"
                        label="訂單日"
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingSelect
                        id="customerId"
                        label="客戶"
                        options={customerOptions}
                        control={control}
                        errors={errors}
                        isDisabled={customerOptionsQuery.isLoading}
                        noOptionsMessage="無可用客戶"
                        onChange={handleCustomerChange}
                      />
                      <FloatingSelect
                        id="billingAddressId"
                        label="帳單地址"
                        options={billingAddressOptions}
                        control={control}
                        errors={errors}
                        isDisabled={addressSelectDisabled}
                        noOptionsMessage={
                          customerId ? '此客戶無帳單／共用地址' : '請先選擇客戶'
                        }
                      />
                      <FloatingSelect
                        id="shippingAddressId"
                        label="收貨地址"
                        options={shippingAddressOptions}
                        control={control}
                        errors={errors}
                        isDisabled={addressSelectDisabled}
                        noOptionsMessage={
                          customerId ? '此客戶無出貨／共用地址' : '請先選擇客戶'
                        }
                      />
                      <FloatingSelect
                        id="shippingContactId"
                        label="收貨聯絡人"
                        options={shippingContactOptions}
                        control={control}
                        errors={errors}
                        isDisabled={addressSelectDisabled}
                        noOptionsMessage={
                          customerId ? '此客戶尚無聯絡人' : '請先選擇客戶'
                        }
                      />
                      <div
                        className={cn(
                          'grid grid-cols-1 gap-3',
                          '@4xl/sheet:col-span-2',
                          '@4xl/sheet:grid-cols-3',
                        )}
                      >
                        <FloatingSelect
                          id="currencyId"
                          label="幣別"
                          options={currencyOptionsQuery.data ?? []}
                          control={control}
                          errors={errors}
                          isDisabled={currencyOptionsQuery.isLoading}
                          noOptionsMessage="無可用幣別"
                          // 點選清單挑幣別，不開放輸入關鍵字
                          isSearchable={false}
                          containerClassName="min-w-40"
                          styles={CURRENCY_SELECT_STYLES}
                        />
                        <FloatingSelect
                          id="taxType"
                          label="課稅別"
                          options={CUSTOMER_ORDER_TAX_TYPE_OPTIONS}
                          control={control}
                          errors={errors}
                          onChange={handleTaxTypeChange}
                        />
                        <FloatingSelect
                          id="roundingMode"
                          label="進位方式"
                          options={CUSTOMER_ORDER_ROUNDING_MODE_OPTIONS}
                          control={control}
                          errors={errors}
                          allowEmpty
                          isClearable={false}
                        />
                      </div>
                      <p className="@4xl/sheet:col-span-2 text-xs text-muted-foreground">
                        {CUSTOMER_ORDER_TAX_TYPE_TIP}
                      </p>
                      <FloatingTextarea
                        id="issueReason"
                        label="訂單事由"
                        register={register}
                        errors={errors}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </section>

                  <CustomerOrderLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    productOptions={productOptions}
                    isProductSelectDisabled={isProductSelectDisabled}
                    productNoOptionsMessage={productNoOptionsMessage}
                    canAddLine={Boolean(customerId)}
                  />

                  {isEdit && id ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      businessId={String(id)}
                      ownerKey="customerOrder"
                      api={customerOrderAttachmentsApi}
                      policy={ATTACHMENT_POLICY_SPREADSHEET}
                    />
                  ) : !isEdit ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      ownerKey="customerOrder"
                      api={customerOrderAttachmentsApi}
                      policy={ATTACHMENT_POLICY_SPREADSHEET}
                      pendingFiles={pendingFiles}
                      onPendingFilesChange={setPendingFiles}
                    />
                  ) : null}
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.customerOrder}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditCustomerOrderPage;
